import React, { useState } from "react";
import {
  Box, Paper, Typography, Button, Grid, CircularProgress, TextField,
  Chip, Alert, Card, CardContent,
} from "@mui/material";
import {
  Phone, ArrowForward, Upload, PersonAdd, Email, Sms, Campaign,
} from "@mui/icons-material";
import { uploadLeadsCsv, createLead } from "../../../api/smb.api";

const CallingAgentStep = ({ onComplete, businessId }) => {
  const [demoCallActive, setDemoCallActive] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const handleDemoCall = async () => {
    setDemoCallActive(true);
    // In production, this calls SmbSetupCallingAgentSession for WebSocket URL
    setTimeout(() => setDemoCallActive(false), 3000);
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const result = await uploadLeadsCsv(businessId, file);
      if (result?.status) {
        setUploadResult(`${result.data?.imported || 0} leads imported`);
      }
    } catch (err) {
      console.error("CSV upload error:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddLead = async () => {
    if (!leadName || !leadPhone) return;
    await createLead(businessId, { name: leadName, phone: leadPhone, email: leadEmail });
    setLeadName("");
    setLeadPhone("");
    setLeadEmail("");
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      color: "#fff", bgcolor: "#141626",
      "& fieldset": { borderColor: "#282D51" },
      "&:hover fieldset": { borderColor: "#3B82F6" },
      "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
    },
    "& .MuiInputLabel-root": { color: "#9CA3AF" },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Demo Call */}
      <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>AI Calling Agent</Typography>
        <Typography variant="body2" sx={{ color: "#9CA3AF", mb: 3 }}>
          Try a demo call to hear your AI calling agent in action
        </Typography>
        <Button
          variant="contained"
          startIcon={demoCallActive ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <Phone />}
          onClick={handleDemoCall}
          disabled={demoCallActive}
          fullWidth
          sx={{
            py: 1.5, bgcolor: "#059669", "&:hover": { bgcolor: "#047857" },
            textTransform: "none", fontWeight: 600, borderRadius: 2,
          }}
        >
          {demoCallActive ? "Demo Call in Progress..." : "Start Demo Call"}
        </Button>
      </Paper>

      {/* Lead Import */}
      <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Import Your Leads</Typography>
        <Typography variant="body2" sx={{ color: "#9CA3AF", mb: 2 }}>
          Upload a CSV file or add leads manually
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={isUploading ? <CircularProgress size={16} /> : <Upload />}
            disabled={isUploading}
            sx={{
              flex: 1, color: "#3B82F6", borderColor: "#282D51", textTransform: "none",
              "&:hover": { borderColor: "#3B82F6" },
            }}
          >
            Upload CSV
            <input type="file" accept=".csv" hidden onChange={handleCsvUpload} />
          </Button>
        </Box>
        {uploadResult && (
          <Alert severity="success" sx={{ mb: 2, bgcolor: "rgba(16,185,129,0.1)", color: "#6EE7B7" }}>
            {uploadResult}
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ color: "#9CA3AF", mb: 1.5 }}>
          Or add a lead manually:
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TextField label="Name" value={leadName} onChange={(e) => setLeadName(e.target.value)} size="small" fullWidth sx={inputSx} />
          <TextField label="Phone" value={leadPhone} onChange={(e) => setLeadPhone(e.target.value)} size="small" fullWidth sx={inputSx} />
          <TextField label="Email" value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} size="small" fullWidth sx={inputSx} />
          <Button
            startIcon={<PersonAdd />}
            onClick={handleAddLead}
            disabled={!leadName || !leadPhone}
            sx={{ color: "#3B82F6", textTransform: "none", alignSelf: "flex-start" }}
          >
            Add Lead
          </Button>
        </Box>
      </Paper>

      {/* Coming Soon: Email & SMS */}
      <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 3, opacity: 0.7 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Campaign sx={{ color: "#F59E0B" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Email & SMS Campaigns</Typography>
          <Chip label="Coming Soon" size="small" sx={{ bgcolor: "#F59E0B", color: "#000", fontWeight: 600 }} />
        </Box>
        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
          Run targeted email and SMS campaigns to your leads. This feature is launching soon.
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Card sx={{ bgcolor: "#141626", border: "1px solid #282D51", textAlign: "center", py: 2 }}>
              <Email sx={{ color: "#9CA3AF", fontSize: 32, mb: 1 }} />
              <Typography variant="caption" sx={{ color: "#9CA3AF" }}>Email Campaigns</Typography>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card sx={{ bgcolor: "#141626", border: "1px solid #282D51", textAlign: "center", py: 2 }}>
              <Sms sx={{ color: "#9CA3AF", fontSize: 32, mb: 1 }} />
              <Typography variant="caption" sx={{ color: "#9CA3AF" }}>SMS Campaigns</Typography>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      <Button
        variant="contained" endIcon={<ArrowForward />} onClick={onComplete} fullWidth
        sx={{
          py: 1.5, bgcolor: "#3B82F6", "&:hover": { bgcolor: "#2563EB" },
          textTransform: "none", fontWeight: 600, borderRadius: 2,
        }}
      >
        Continue to Payment
      </Button>
    </Box>
  );
};

export default CallingAgentStep;
